import { useState } from 'react';
import { Button } from './ui/button';
import { ChevronRight, RefreshCw, Utensils, Heart, Clock, Brain } from 'lucide-react';

const questions = [
  {
    id: 1,
    question: "Depuis combien de temps n'avez-vous pas mangé ?",
    icon: Clock,
    options: [
      { text: "Moins de 2 heures", score: 0 },
      { text: "2 à 4 heures", score: 1 },
      { text: "Plus de 4 heures", score: 2 }
    ]
  },
  {
    id: 2,
    question: "Cette envie est apparue...",
    icon: Brain,
    options: [
      { text: "Soudainement (en voyant ou sentant quelque chose)", score: 0 },
      { text: "Progressivement", score: 2 }
    ]
  },
  {
    id: 3,
    question: "Accepteriez-vous de manger des choux de Bruxelles à la place ?",
    icon: Utensils,
    options: [
      { text: "Beurk, non merci !", score: 0 },
      { text: "Pourquoi pas, j'ai vraiment faim", score: 2 }
    ]
  },
  {
    id: 4,
    question: "Comment vous sentez-vous en ce moment ?",
    icon: Heart,
    options: [
      { text: "Stressé(e), fatigué(e) ou ennuyé(e)", score: 0 },
      { text: "Plutôt bien, neutre", score: 1 },
      { text: "Mon estomac gargouille !", score: 2 }
    ]
  }
];

const getResult = (score) => {
  if (score <= 2) {
    return {
      title: "C'est une ENVIE",
      emoji: "🧠",
      color: "text-declic",
      bgColor: "bg-declic/10",
      message: "Votre corps n'a pas vraiment faim. Cette envie est probablement émotionnelle ou déclenchée par un stimulus externe (odeur, vue, ennui...).",
      advice: "Rappelez-vous : entre les repas, on ne mange pas ! Buvez un verre d'eau, occupez-vous, ou utilisez l'outil Urgence si besoin. L'envie passera dans 15-20 minutes."
    };
  } else if (score <= 5) {
    return {
      title: "Zone grise...",
      emoji: "🤔",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      message: "Difficile à dire ! C'est peut-être un mélange de vraie faim et d'envie.",
      advice: "Attendez votre prochain repas. Si la sensation persiste et s'intensifie, c'est probablement de la vraie faim. Un café ou un thé peut vous aider à patienter !"
    };
  } else {
    return {
      title: "C'est de la VRAIE FAIM",
      emoji: "✅",
      color: "text-sage-600",
      bgColor: "bg-sage-50",
      message: "Votre corps vous envoie de vrais signaux de faim. C'est le moment de passer à table !",
      advice: "Si c'est l'heure de votre repas, foncez ! Prenez le temps de savourer et arrêtez-vous quand vous êtes satisfait(e)."
    };
  }
};

export const HungerQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
  };

  const totalScore = answers.reduce((a, b) => a + b, 0);
  const result = getResult(totalScore);
  const question = questions[currentQuestion];
  const QuestionIcon = question?.icon;

  if (showResult) {
    return (
      <div className="p-6 space-y-6">
        <div className={`${result.bgColor} rounded-2xl p-6 text-center`}>
          <span className="text-5xl mb-4 block">{result.emoji}</span>
          <h3 className={`text-2xl font-serif font-medium ${result.color} mb-2`}>
            {result.title}
          </h3>
          <p className="text-sage-600 mb-4">{result.message}</p>
        </div>

        <div className="bg-sage-50 rounded-xl p-4 border-l-4 border-sage-400">
          <p className="text-sage-700 font-medium mb-1">Notre conseil</p>
          <p className="text-sage-600 text-sm">{result.advice}</p>
        </div>

        {totalScore <= 2 && (
          <a href="/outils/que-faire-si" className="block bg-declic/10 rounded-xl p-4 border border-declic/20 text-center hover:bg-declic/15 transition-colors">
            <p className="text-declic font-medium text-sm">Besoin d'aide maintenant ?</p>
            <p className="text-sage-500 text-xs mt-1">Ouvrir l'outil anti-pulsions</p>
          </a>
        )}

        <Button
          onClick={resetQuiz}
          variant="outline"
          className="w-full h-12 rounded-xl border-sage-300 text-sage-600 hover:bg-sage-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refaire le test
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Progress */}
      <div className="flex gap-2">
        {questions.map((_, idx) => (
          <div
            key={idx}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              idx <= currentQuestion ? 'bg-sage-500' : 'bg-sage-200'
            }`}
          />
        ))}
      </div>

      {/* Question */}
      <div className="text-center py-4">
        <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <QuestionIcon className="w-8 h-8 text-sage-600" />
        </div>
        <p className="text-sm text-sage-500 mb-2">Question {currentQuestion + 1}/{questions.length}</p>
        <h3 className="text-xl font-medium text-sage-800">{question.question}</h3>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(option.score)}
            className="w-full p-4 text-left bg-white border-2 border-sage-200 rounded-xl hover:border-sage-400 hover:bg-sage-50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <span className="text-sage-700">{option.text}</span>
              <ChevronRight className="w-5 h-5 text-sage-400 group-hover:text-sage-600 transition-colors" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default HungerQuiz;
